<template>
  <div class="crawl-schedules flex-grow-1 d-flex flex-column overflow-y-hidden">
    <div class="a-header">
      <span class="a-header--title slide-down-enter-active">Crawl Schedules</span>
    </div>

    <div class="a-body p-20-px flex-grow-1 overflow-y-auto">
      <a-collapse v-model="activePanel" class="mb-20-px">
        <a-collapse-panel key="create" header="Create New Schedule">
          <a-form layout="vertical" @submit.prevent="onCreateSchedule">
            <a-row :gutter="24">
              <a-col :span="16">
                <DomainInput v-model="formDomains" :max="30" ref="createDomainInput" />
              </a-col>
            </a-row>

            <CrawlOptions
              :depth.sync="formMaxDepth"
              :limit.sync="formPageLimit"
              :depth-mode.sync="formDepthMode"
              :url-report-mode.sync="formUrlReportMode"
            />

            <a-row :gutter="24">
              <a-col :span="8">
                <a-form-item label="Email Notification Contact Group">
                  <a-select v-model="formContactGroupId" size="large" placeholder="Select contact group" style="width: 100%" showSearch :filterOption="filterOption">
                    <a-select-option :value="null">No email notifications</a-select-option>
                    <a-select-option v-for="cg in contactGroups" :key="cg.value" :value="cg.value">
                      {{ cg.label }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </a-col>
            </a-row>

            <a-row :gutter="24">
              <a-col :span="16">
                <a-form-item label="Frequency">
                  <a-radio-group v-model="formFrequency" size="large">
                    <a-radio-button v-for="freq in ScheduleFrequencies" :key="freq.value" :value="freq.value">
                      {{ freq.label }}
                    </a-radio-button>
                  </a-radio-group>
                </a-form-item>
              </a-col>
            </a-row>

            <a-row :gutter="24">
              <a-col v-if="formFrequency === 'weekly'" :span="8">
                <a-form-item label="Day of Week">
                  <a-select v-model="formDayOfWeek" size="large" style="width: 100%">
                    <a-select-option v-for="day in DaysOfWeek" :key="day.value" :value="day.value">
                      {{ day.label }}
                    </a-select-option>
                  </a-select>
                </a-form-item>
              </a-col>
              <a-col v-if="formFrequency === 'once'" :span="8">
                <a-form-item label="Date">
                  <a-date-picker
                    v-model="formOneTimeDate"
                    size="large"
                    style="width: 100%"
                    :disabled-date="disablePastDates"
                  />
                </a-form-item>
              </a-col>
              <a-col :span="8">
                <a-form-item label="Time">
                  <a-time-picker
                    v-model="formTime"
                    format="HH:mm"
                    size="large"
                    style="width: 100%"
                    :minute-step="15"
                  />
                </a-form-item>
              </a-col>
            </a-row>

            <a-button type="primary" size="large" html-type="submit" :loading="isCreating">
              Create Schedule
            </a-button>
          </a-form>
        </a-collapse-panel>
      </a-collapse>

      <h3 class="section-title mb-3">Active Schedules</h3>

      <div v-if="isLoadingSchedules" class="d-flex justify-content-center align-items-center pt-35-px">
        <Spinner />
      </div>

      <transition name="slide-up">
        <div v-if="!isLoadingSchedules">
          <a-table
            v-if="schedules.length > 0"
            :columns="scheduleColumns"
            :dataSource="pagedSchedules"
            :rowKey="(record) => record.id"
            :pagination="false"
            size="middle"
          >
            <template slot="domains" slot-scope="text, record">
              <template v-if="record.domains && record.domains.length > 0">
                <div v-for="(d, i) in record.domains.slice(0, 3)" :key="i" class="schedule-domain-name">{{ d }}</div>
                <a-tooltip v-if="record.domains.length > 3">
                  <template slot="title">
                    <div v-for="(d, i) in record.domains" :key="i">{{ d }}</div>
                  </template>
                  <span class="schedule-domain-more">+{{ record.domains.length - 3 }} more</span>
                </a-tooltip>
              </template>
              <template v-else>-</template>
            </template>
            <template slot="frequency" slot-scope="text">
              <a-tag>{{ text }}</a-tag>
            </template>
            <template slot="schedule" slot-scope="text, record">
              <template v-if="record.frequency === 'daily'">
                Every day at {{ getLocalTime(record) }}
              </template>
              <template v-else-if="record.frequency === 'weekly'">
                Every {{ getLocalDayOfWeek(record) }} at {{ getLocalTime(record) }}
              </template>
              <template v-else-if="record.frequency === 'once'">
                {{ getLocalTime(record) }}
              </template>
              <template v-else>-</template>
            </template>
            <template slot="next_run_at" slot-scope="text">
              <template v-if="text">{{ formatLocalDateTime(text) }}</template>
              <template v-else>-</template>
            </template>
            <template slot="last_run_at" slot-scope="text">
              <template v-if="text">{{ formatLocalDateTime(text) }}</template>
              <template v-else>-</template>
            </template>
            <template slot="contactGroup" slot-scope="text">
              {{ getContactGroupName(text) }}
            </template>
            <template slot="action" slot-scope="text, record">
              <a-button type="primary" size="small" ghost class="mr-1" @click="openEditSchedule(record)">
                <a-icon type="edit" />
              </a-button>
              <a-popconfirm
                title="Are you sure you want to delete this schedule?"
                ok-text="Yes"
                cancel-text="No"
                @confirm="onDeleteSchedule(record.id)"
              >
                <a-button type="danger" size="small" ghost :disabled="deletingScheduleId === record.id">
                  <a-icon type="delete" />
                </a-button>
              </a-popconfirm>
            </template>
          </a-table>
          <a-empty v-else description="No active schedules" />
          <div v-if="schedules.length > 0" class="a-footer mt-3">
            <PaginationBar
              :current="pagin.current"
              :pageSize.sync="pagin.pageSize"
              :total="pagin.total"
              @change="paginChange"
            />
          </div>
        </div>
      </transition>

      <a-modal
        :visible="editModalVisible"
        :width="880"
        :destroy-on-close="true"
        title="Edit schedule"
        :footer="null"
        @cancel="closeEditModal"
      >
        <a-form layout="vertical" @submit.prevent="submitEditSchedule">
          <a-row :gutter="24">
            <a-col :span="16">
              <DomainInput v-model="editFormDomains" :max="30" ref="editDomainInput" />
            </a-col>
          </a-row>

          <CrawlOptions
            :depth.sync="editFormMaxDepth"
            :limit.sync="editFormPageLimit"
            :depth-mode.sync="editFormDepthMode"
            :url-report-mode.sync="editFormUrlReportMode"
          />

          <a-row :gutter="24">
            <a-col :span="8">
              <a-form-item label="Email Notification Contact Group">
                <a-select v-model="editFormContactGroupId" size="large" placeholder="Select contact group" style="width: 100%" showSearch :filterOption="filterOption">
                  <a-select-option :value="null">No email notifications</a-select-option>
                  <a-select-option v-for="cg in contactGroups" :key="'ed-cg-' + cg.value" :value="cg.value">
                    {{ cg.label }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
          </a-row>

          <a-row :gutter="24">
            <a-col :span="16">
              <a-form-item label="Frequency">
                <a-radio-group v-model="editFormFrequency" size="large">
                  <a-radio-button v-for="freq in ScheduleFrequencies" :key="'ed-f-' + freq.value" :value="freq.value">
                    {{ freq.label }}
                  </a-radio-button>
                </a-radio-group>
              </a-form-item>
            </a-col>
          </a-row>

          <a-row :gutter="24">
            <a-col v-if="editFormFrequency === 'weekly'" :span="8">
              <a-form-item label="Day of Week">
                <a-select v-model="editFormDayOfWeek" size="large" style="width: 100%">
                  <a-select-option v-for="day in DaysOfWeek" :key="'ed-d-' + day.value" :value="day.value">
                    {{ day.label }}
                  </a-select-option>
                </a-select>
              </a-form-item>
            </a-col>
            <a-col v-if="editFormFrequency === 'once'" :span="8">
              <a-form-item label="Date">
                <a-date-picker
                  v-model="editFormOneTimeDate"
                  size="large"
                  style="width: 100%"
                  :disabled-date="disablePastDates"
                />
              </a-form-item>
            </a-col>
            <a-col :span="8">
              <a-form-item label="Time">
                <a-time-picker
                  v-model="editFormTime"
                  format="HH:mm"
                  size="large"
                  style="width: 100%"
                  :minute-step="15"
                />
              </a-form-item>
            </a-col>
          </a-row>

          <div class="d-flex justify-content-end mt-3">
            <a-button class="mr-2" @click="closeEditModal">Cancel</a-button>
            <a-button type="primary" html-type="submit" :loading="isUpdating">Save</a-button>
          </div>
        </a-form>
      </a-modal>
    </div>
  </div>
</template>

<script>
  import moment from 'moment-timezone';
  import { formatUtcTime } from '@shared/helpers/utils';
  import WebCrawlService from '../services/web-crawl.service';
  import {
    DEFAULT_DEPTH_MODE,
    DEFAULT_URL_REPORT_MODE,
    getDepthModeLabel,
    getUrlReportModeLabel,
    ScheduleFrequencies,
    DaysOfWeek,
  } from '../consts/CrawlStatus.consts';
  import { formatApiError } from '../helpers/format-api-error';
  import {
    clampCrawlDepth,
    clampPageLimit,
    normalizeUrlForDedup,
    DEFAULT_DEPTH,
    DEFAULT_PAGE_LIMIT,
  } from '../helpers/crawl-validation';
  import ContactGroupService from '@modules/ContactGroup/services/contact-group.service';
  import DomainInput from './DomainInput.vue';
  import CrawlOptions from './CrawlOptions.vue';

  export default {
    name: 'CrawlSchedules',
    components: { DomainInput, CrawlOptions },
    data() {
      return {
        ScheduleFrequencies,
        DaysOfWeek,
        activePanel: ['create'],
        contactGroups: [],
        defaultContactGroupId: null,
        formDomains: [{ url: '' }],
        formMaxDepth: DEFAULT_DEPTH,
        formPageLimit: DEFAULT_PAGE_LIMIT,
        formDepthMode: DEFAULT_DEPTH_MODE,
        formUrlReportMode: DEFAULT_URL_REPORT_MODE,
        formFrequency: 'weekly',
        formDayOfWeek: 1,
        formOneTimeDate: null,
        formTime: null,
        formContactGroupId: null,
        isCreating: false,
        editModalVisible: false,
        editingScheduleId: null,
        editFormDomains: [{ url: '' }],
        editFormMaxDepth: DEFAULT_DEPTH,
        editFormPageLimit: DEFAULT_PAGE_LIMIT,
        editFormDepthMode: DEFAULT_DEPTH_MODE,
        editFormUrlReportMode: DEFAULT_URL_REPORT_MODE,
        editFormFrequency: 'weekly',
        editFormDayOfWeek: 1,
        editFormOneTimeDate: null,
        editFormTime: null,
        editFormContactGroupId: null,
        isUpdating: false,
        deletingScheduleId: null,
        schedules: [],
        isLoadingSchedules: true,
        pagin: {
          current: 1,
          pageSize: 10,
          total: 0,
        },
        scheduleColumns: [
          {
            title: '#',
            dataIndex: '__rowNumber',
            width: 60,
          },
          {
            title: 'Domains',
            dataIndex: 'domains',
            scopedSlots: { customRender: 'domains' },
            width: 250,
          },
          {
            title: 'Frequency',
            dataIndex: 'frequency',
            scopedSlots: { customRender: 'frequency' },
            width: 100,
          },
          {
            title: 'Schedule',
            dataIndex: 'schedule',
            scopedSlots: { customRender: 'schedule' },
            width: 180,
          },
          {
            title: 'Next Run',
            dataIndex: 'next_run_at',
            scopedSlots: { customRender: 'next_run_at' },
            width: 170,
          },
          {
            title: 'Last Run',
            dataIndex: 'last_run_at',
            scopedSlots: { customRender: 'last_run_at' },
            width: 170,
          },
          {
            title: 'Depth',
            dataIndex: 'max_depth',
            width: 70,
          },
          {
            title: 'Depth Mode',
            dataIndex: 'depth_mode',
            customRender: getDepthModeLabel,
            width: 110,
          },
          {
            title: 'Limit',
            dataIndex: 'page_limit',
            width: 70,
          },
          {
            title: 'URL Report',
            dataIndex: 'url_report_mode',
            customRender: getUrlReportModeLabel,
            width: 130,
          },
          {
            title: 'Contact Group',
            dataIndex: 'contact_group_id',
            scopedSlots: { customRender: 'contactGroup' },
            width: 140,
          },
          {
            title: '',
            dataIndex: 'action',
            scopedSlots: { customRender: 'action' },
            width: 112,
          },
        ],
      };
    },
    async mounted() {
      await this.fetchContactGroups();
      this.fetchSchedules();
    },
    computed: {
      pagedSchedules() {
        const pageSize = Math.max(1, Number(this.pagin.pageSize) || 10);
        const current = Math.max(1, Number(this.pagin.current) || 1);
        const start = (current - 1) * pageSize;
        return this.schedules.slice(start, start + pageSize).map((record, index) => ({
          ...record,
          __rowNumber: start + index + 1,
        }));
      },
    },
    methods: {
      /**
       * Backend stores and compares schedules in UTC (time, day_of_week, next_run_at).
       * We send `time` as UTC HH:mm from the picker, `day_of_week` as the UTC weekday of the
       * instant that combines the selected weekday + local wall time, and `one_time_date` as UTC ISO.
       * Used for both create and update: always the full payload (domains, frequency, time, limits, etc.).
       */
      buildSchedulePayload({
        domains,
        timeMoment,
        maxDepth,
        pageLimit,
        depthMode,
        urlReportMode,
        frequency,
        dayOfWeek,
        oneTimeDate,
        contactGroupId,
      }) {
        const safeDepth = clampCrawlDepth(maxDepth);
        const safeLimit = clampPageLimit(pageLimit);
        // Normalize to system-local so hour()/minute() are in the same timezone
        // as moment() and the pickers (a-time-picker, a-date-picker), which
        // always operate in the system timezone.
        const sysLocal = timeMoment.clone().local();
        const utcTime = sysLocal.clone().utc();
        const timeStr = utcTime.format('HH:mm');
        const seen = new Set();
        const dedupedDomains = domains
          .map((d) => d.url.trim())
          .filter(Boolean)
          .filter((url) => {
            const key = normalizeUrlForDedup(url);
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        const payload = {
          domains: dedupedDomains,
          max_depth: safeDepth,
          depth_mode: depthMode,
          limit: safeLimit,
          url_report_mode: urlReportMode,
          frequency,
          time: timeStr,
          contact_group_id: contactGroupId,
        };
        if (frequency === 'weekly') {
          const localDate = moment().day(dayOfWeek).hour(sysLocal.hour()).minute(sysLocal.minute()).second(0);
          payload.day_of_week = localDate.clone().utc().day();
        } else if (frequency === 'once') {
          const combined = oneTimeDate.clone()
            .hour(sysLocal.hour())
            .minute(sysLocal.minute())
            .second(0);
          payload.one_time_date = combined.clone().utc().format('YYYY-MM-DD[T]HH:mm:ss[Z]');
        }
        return payload;
      },
      disablePastDates(current) {
        return current && current < moment().startOf('day');
      },
      async onCreateSchedule() {
        if (this.isCreating) return;
        const validationError = this.$refs.createDomainInput.validateAll();
        if (validationError) {
          this.$showErrorMessage(validationError);
          return;
        }
        if (!this.formTime) {
          this.$showErrorMessage('Please select a time.');
          return;
        }
        if (this.formFrequency === 'once' && !this.formOneTimeDate) {
          this.$showErrorMessage('Please select a date.');
          return;
        }

        const fd = clampCrawlDepth(this.formMaxDepth);
        const fl = clampPageLimit(this.formPageLimit);
        if (fd !== this.formMaxDepth) this.formMaxDepth = fd;
        if (fl !== this.formPageLimit) this.formPageLimit = fl;

        const payload = this.buildSchedulePayload({
          domains: this.formDomains,
          timeMoment: this.formTime,
          maxDepth: fd,
          pageLimit: fl,
          depthMode: this.formDepthMode,
          urlReportMode: this.formUrlReportMode,
          frequency: this.formFrequency,
          dayOfWeek: this.formDayOfWeek,
          oneTimeDate: this.formOneTimeDate,
          contactGroupId: this.formContactGroupId,
        });

        this.isCreating = true;
        try {
          await WebCrawlService.createSchedule(payload);
          this.$showSuccessMessage('Schedule created successfully.');
          this.resetForm();
          this.fetchSchedules();
        } catch (error) {
          this.$showErrorMessage(formatApiError(error, 'Failed to create schedule.'));
        } finally {
          this.isCreating = false;
        }
      },
      resetForm() {
        this.$refs.createDomainInput.reset();
        this.formMaxDepth = DEFAULT_DEPTH;
        this.formPageLimit = DEFAULT_PAGE_LIMIT;
        this.formDepthMode = DEFAULT_DEPTH_MODE;
        this.formUrlReportMode = DEFAULT_URL_REPORT_MODE;
        this.formFrequency = 'weekly';
        this.formDayOfWeek = 1;
        this.formOneTimeDate = null;
        this.formTime = null;
        this.formContactGroupId = this.defaultContactGroupId;
      },
      async fetchContactGroups() {
        try {
          const groups = await ContactGroupService.getList();
          const list = groups || [];
          const defaultGroup = list.find((g) => g.description === 'DEFAULT');
          this.contactGroups = list.map((g) => ({ label: g.description, value: g.id }));
          if (defaultGroup) {
            this.defaultContactGroupId = defaultGroup.id;
            if (this.formContactGroupId == null) {
              this.formContactGroupId = defaultGroup.id;
            }
          }
        } catch {
          this.$showErrorMessage('Failed to load contact groups');
        }
      },
      filterOption(input, option) {
        return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
      },
      getContactGroupName(id) {
        if (id == null) return '-';
        const group = this.contactGroups.find((g) => g.value === id);
        return group ? group.label : '-';
      },
      async fetchSchedules() {
        try {
          const response = await WebCrawlService.getSchedules();
          this.schedules = (response && response.data) || [];
          this.pagin.total = this.schedules.length;
          const lastPage = Math.max(1, Math.ceil(this.pagin.total / this.pagin.pageSize));
          if (this.pagin.current > lastPage) {
            this.pagin.current = lastPage;
          }
        } catch (error) {
          this.$showErrorMessage(formatApiError(error, 'Failed to fetch schedules.'));
        } finally {
          this.isLoadingSchedules = false;
        }
      },
      paginChange(pagin) {
        this.pagin = {
          ...this.pagin,
          current: Math.max(1, Number(pagin.current) || 1),
          pageSize: Math.max(1, Number(pagin.pageSize) || 10),
        };
      },
      // Uses .local() (system timezone) to match a-time-picker, which is also
      // system timezone. Do NOT use UserDate.userTimezone here — it can differ
      // from system timezone and would cause the displayed time to not match
      // what the user entered. Keep getLocalMoment, formatLocalDateTime,
      // buildSchedulePayload, and openEditSchedule in sync.
      getLocalMoment(record) {
        if (record.next_run_at) {
          const utcValue = formatUtcTime(record.next_run_at);
          return moment.utc(utcValue).local();
        }
        if (record.frequency === 'once' && record.one_time_date) {
          const utcValue = formatUtcTime(record.one_time_date);
          return moment.utc(utcValue).local();
        }
        if (record.scheduled_time) {
          const m = moment.utc(record.scheduled_time, 'HH:mm:ss');
          if (record.frequency === 'weekly' && record.day_of_week != null) {
            m.day(record.day_of_week);
          }
          return m.local();
        }
        return null;
      },
      formatLocalDateTime(utcValue) {
        if (!utcValue) return '-';
        return moment.utc(formatUtcTime(utcValue)).local().format('YYYY-MM-DD HH:mm:ss');
      },
      getLocalTime(record) {
        const m = this.getLocalMoment(record);
        return m ? m.format('HH:mm') : '-';
      },
      getLocalDayOfWeek(record) {
        const m = this.getLocalMoment(record);
        return m ? m.format('dddd') : '-';
      },
      openEditSchedule(record) {
        this.editingScheduleId = record.id;
        const urls = record.domains && record.domains.length ? record.domains : [];
        this.editFormDomains = urls.length ? urls.map((url) => ({ url })) : [{ url: '' }];
        this.editFormMaxDepth = record.max_depth != null ? record.max_depth : DEFAULT_DEPTH;
        this.editFormPageLimit = record.page_limit;
        this.editFormDepthMode = record.depth_mode;
        this.editFormUrlReportMode = record.url_report_mode;
        this.editFormFrequency = record.frequency;

        // Convert backend UTC values to system-local for the edit form.
        // Pickers (a-time-picker, a-date-picker) and buildSchedulePayload both
        // operate in system-local, so the form must use the same timezone.
        let m = null;
        if (record.frequency === 'once' && record.one_time_date) {
          const utcValue = formatUtcTime(record.one_time_date);
          m = moment.utc(utcValue).local();
        } else if (record.scheduled_time) {
          const utcM = moment.utc(record.scheduled_time, 'HH:mm:ss');
          if (record.frequency === 'weekly' && record.day_of_week != null) {
            utcM.day(record.day_of_week);
          }
          m = utcM.local();
        }
        this.editFormTime = m ? m.clone() : null;
        this.editFormDayOfWeek = m ? m.day() : 1;
        this.editFormOneTimeDate = record.frequency === 'once' && m ? m.clone().startOf('day') : null;
        this.editFormContactGroupId = record.contact_group_id ?? null;
        this.editModalVisible = true;
      },
      closeEditModal() {
        this.editModalVisible = false;
        this.editingScheduleId = null;
      },
      async submitEditSchedule() {
        if (this.isUpdating || !this.editingScheduleId) return;
        const domainInput = this.$refs.editDomainInput;
        if (!domainInput) return;
        const validationError = domainInput.validateAll();
        if (validationError) {
          this.$showErrorMessage(validationError);
          return;
        }
        if (!this.editFormTime) {
          this.$showErrorMessage('Please select a time.');
          return;
        }
        if (this.editFormFrequency === 'once' && !this.editFormOneTimeDate) {
          this.$showErrorMessage('Please select a date.');
          return;
        }

        const ed = clampCrawlDepth(this.editFormMaxDepth);
        const el = clampPageLimit(this.editFormPageLimit);
        if (ed !== this.editFormMaxDepth) this.editFormMaxDepth = ed;
        if (el !== this.editFormPageLimit) this.editFormPageLimit = el;

        const payload = this.buildSchedulePayload({
          domains: this.editFormDomains,
          timeMoment: this.editFormTime,
          maxDepth: ed,
          pageLimit: el,
          depthMode: this.editFormDepthMode,
          urlReportMode: this.editFormUrlReportMode,
          frequency: this.editFormFrequency,
          dayOfWeek: this.editFormDayOfWeek,
          oneTimeDate: this.editFormOneTimeDate,
          contactGroupId: this.editFormContactGroupId,
        });

        this.isUpdating = true;
        try {
          await WebCrawlService.updateSchedule(this.editingScheduleId, payload);
          this.$showSuccessMessage('Schedule updated successfully.');
          this.closeEditModal();
          this.fetchSchedules();
        } catch (error) {
          this.$showErrorMessage(formatApiError(error, 'Failed to update schedule.'));
        } finally {
          this.isUpdating = false;
        }
      },
      async onDeleteSchedule(id) {
        if (this.deletingScheduleId) return;
        this.deletingScheduleId = id;
        try {
          await WebCrawlService.deleteSchedule(id);
          this.$showSuccessMessage('Schedule deleted.');
          this.fetchSchedules();
        } catch (error) {
          this.$showErrorMessage(formatApiError(error, 'Failed to delete schedule.'));
        } finally {
          this.deletingScheduleId = null;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .section-title {
    font-size: 16px;
    font-weight: 600;
  }

  .schedule-domain-name {
    font-size: 13px;
    line-height: 1.4;
    word-break: break-all;
  }

  .schedule-domain-more {
    font-size: 12px;
    color: $prLgt;
  }
</style>
